import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  AppBar,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Slide,
  Toolbar,
  Tooltip,
  Typography,
  useScrollTrigger,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import SettingsIcon from "@mui/icons-material/Settings";
import HomeIcon from "@mui/icons-material/Home";
import NoteAltIcon from "@mui/icons-material/NoteAlt";
import GroupsIcon from "@mui/icons-material/Groups";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ThemeToggle from "./ThemeToggle";
import SettingsPanel from "./SettingsPanel";
import LanguageSwitch from "./LanguageSwitch";
import UserMenu from "./Auth/UserMenu";
import SocialButton from "./Social/SocialButton";
import { useZenMode } from "../theme/ZenModeContext";
import { useEdition } from "../bible/edition";
import { useBible } from "../data/bibleContext";

export const MOBILE_BOTTOM_NAV_HEIGHT = 56;

function HideOnScroll({ children }) {
  const trigger = useScrollTrigger();
  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

const resolveHeaderTitle = ({
  pathname,
  edition,
  bibleData,
  bibleDataEn,
}) => {
  if (pathname === "/about") return "关于";
  if (pathname === "/notes") return "我的笔记";
  if (pathname === "/history") return "阅读记录";
  if (pathname === "/community") return "社区分享";
  if (pathname.startsWith("/shared/notes")) return "灵修笔记分享";
  if (pathname === "/admin") return "后台管理";
  if (pathname === "/profile") return "个人资料";

  const chapterMatch = pathname.match(/\/chapter\/([^/]+)/);
  const titleSource = edition.id === "en" ? bibleDataEn : bibleData;
  if (chapterMatch && titleSource) {
    const currentCh = titleSource.find((ch) => ch.id === chapterMatch[1]);
    if (currentCh) {
      const rawBook = currentCh.bookName || "";
      const book = rawBook.includes("-")
        ? rawBook.split("-").slice(1).join("-")
        : rawBook;
      return edition.id === "en"
        ? `${book} ${currentCh.chapterNumber}`
        : `${book} 第${currentCh.chapterNumber}章`;
    }
  }

  return edition.appTitle;
};

const HeaderMoreMenu = () => {
  const [anchorEl, setAnchorEl] = useState(null);

  return (
    <>
      <Tooltip title="更多">
        <IconButton
          color="inherit"
          size="small"
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{ p: { xs: 0.35, sm: 0.75 }, display: { xs: "inline-flex", md: "none" } }}
          aria-label="更多"
          aria-haspopup="menu"
          aria-expanded={Boolean(anchorEl)}
          data-testid="header-more"
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{
          sx: { mt: 1, minWidth: 220, borderRadius: 2 },
        }}
      >
        <Box sx={{ px: 1.5, py: 1.25 }} onClick={(event) => event.stopPropagation()}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            语言 / Language
          </Typography>
          <LanguageSwitch size="comfortable" />
        </Box>
        <Divider />
        <Box
          sx={{ px: 0.5, py: 0.25, display: "flex", alignItems: "center" }}
          onClick={(event) => event.stopPropagation()}
        >
          <ThemeToggle size="small" sx={{ p: 0.75 }} />
          <Typography variant="body2">阅读主题</Typography>
        </Box>
        <MenuItem
          component={Link}
          to="/about"
          onClick={() => setAnchorEl(null)}
        >
          <ListItemIcon>
            <InfoIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="关于" />
        </MenuItem>
      </Menu>
    </>
  );
};

const MobileBottomNav = () => {
  const location = useLocation();
  const edition = useEdition();
  const { isZenMode } = useZenMode();

  if (isZenMode) {
    return null;
  }

  let value = "";
  if (
    location.pathname === "/" ||
    location.pathname === "/en" ||
    location.pathname === "/compare"
  ) {
    value = "home";
  } else if (
    location.pathname === "/community" ||
    location.pathname.startsWith("/shared/notes")
  ) {
    value = "community";
  } else if (location.pathname === "/notes") {
    value = "notes";
  }

  return (
    <Paper
      data-testid="mobile-bottom-nav"
      elevation={8}
      sx={{
        display: { xs: "block", sm: "none" },
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1200,
        pb: "env(safe-area-inset-bottom)",
      }}
    >
      <BottomNavigation
        showLabels
        value={value}
        sx={{ height: MOBILE_BOTTOM_NAV_HEIGHT }}
      >
        <BottomNavigationAction
          component={Link}
          to={edition.homePath}
          value="home"
          label="首页"
          icon={<HomeIcon />}
        />
        <BottomNavigationAction
          component={Link}
          to="/community"
          value="community"
          label="社区"
          icon={<GroupsIcon />}
        />
        <BottomNavigationAction
          component={Link}
          to="/notes"
          value="notes"
          label="笔记"
          icon={<NoteAltIcon />}
        />
      </BottomNavigation>
    </Paper>
  );
};

const TopBar = () => {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { isZenMode } = useZenMode();
  const { bibleData, bibleDataEn } = useBible();
  const location = useLocation();
  const navigate = useNavigate();
  const edition = useEdition();
  const isHomePage =
    location.pathname === "/" ||
    location.pathname === "/en" ||
    location.pathname === "/compare";

  if (isZenMode) {
    return null;
  }

  const title = resolveHeaderTitle({
    pathname: location.pathname,
    edition,
    bibleData,
    bibleDataEn,
  });

  return (
    <>
      <HideOnScroll>
        <AppBar
          position="fixed"
          color="inherit"
          elevation={1}
          sx={{
            backdropFilter: "blur(8px)",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            "@media (prefers-color-scheme: dark)": {
              backgroundColor: "rgba(0, 0, 0, 0.8)",
            },
          }}
        >
          <Toolbar
            sx={{
              backgroundColor: (theme) => theme.palette.background.paper,
              px: { xs: 0.75, sm: 2 },
              minHeight: { xs: 52, sm: 60 },
              gap: { xs: 0.35, sm: 1.5 },
            }}
          >
            {!isHomePage && (
              <IconButton
                edge="start"
                color="inherit"
                size="small"
                onClick={() => navigate(edition.homePath)}
                sx={{
                  p: { xs: 0.35, sm: 0.75 },
                  mr: { xs: 0, sm: 0.5 },
                  display: { xs: "none", sm: "inline-flex" },
                }}
                aria-label="首页"
              >
                <HomeIcon fontSize="small" />
              </IconButton>
            )}
            <Box
              component={Link}
              to={edition.homePath}
              sx={{
                flex: "1 1 auto",
                minWidth: 0,
                color: (theme) => theme.palette.text.primary,
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
                gap: { xs: 0.5, sm: 1 },
                overflow: "hidden",
              }}
            >
              <Box
                component="img"
                src="/logo.png"
                alt="logo"
                sx={{
                  width: "auto",
                  height: { xs: 22, sm: 28 },
                  maxHeight: { xs: 22, sm: 28 },
                  flexShrink: 0,
                }}
              />
              <Typography
                component="span"
                data-testid="app-header-title"
                title={title}
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "0.95rem", sm: "1.35rem" },
                  letterSpacing: { xs: 0, sm: "0.05em" },
                  whiteSpace: { xs: "normal", sm: "nowrap" },
                  overflow: "hidden",
                  textOverflow: { xs: "clip", sm: "ellipsis" },
                  display: { xs: "-webkit-box", sm: "block" },
                  WebkitLineClamp: { xs: 2, sm: "none" },
                  WebkitBoxOrient: "vertical",
                  wordBreak: "break-word",
                  lineHeight: 1.2,
                  flex: "1 1 auto",
                  minWidth: 0,
                }}
              >
                {title}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 0, sm: 0.75 },
                flex: "0 0 auto",
              }}
            >
              <Tooltip title="社区灵修分享">
                <IconButton
                  component={Link}
                  to="/community"
                  color="inherit"
                  size="small"
                  sx={{ p: { xs: 0.35, sm: 0.75 }, display: { xs: "none", sm: "inline-flex" } }}
                  aria-label="社区灵修"
                >
                  <GroupsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="我的灵修笔记">
                <IconButton
                  component={Link}
                  to="/notes"
                  color="inherit"
                  size="small"
                  sx={{ p: { xs: 0.35, sm: 0.75 }, display: { xs: "none", sm: "inline-flex" } }}
                  aria-label="我的笔记"
                >
                  <NoteAltIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <SocialButton sx={{ p: { xs: 0.35, sm: 0.75 } }} />
              <Box sx={{ display: { xs: "none", md: "inline-flex" }, alignItems: "center", gap: 0.75 }}>
                <LanguageSwitch />
                <ThemeToggle size="small" sx={{ p: { xs: 0.35, sm: 0.75 } }} />
                <IconButton
                  component={Link}
                  to="/about"
                  color="inherit"
                  size="small"
                  sx={{ p: { xs: 0.35, sm: 0.75 } }}
                  aria-label="关于"
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Box>
              <HeaderMoreMenu />
              <IconButton
                onClick={() => setSettingsOpen(true)}
                color="inherit"
                size="small"
                sx={{ p: { xs: 0.35, sm: 0.75 } }}
                aria-label="阅读设置"
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
              <UserMenu />
            </Box>
          </Toolbar>
        </AppBar>
      </HideOnScroll>
      <Toolbar sx={{ minHeight: { xs: 52, sm: 60 } }} />
      <SettingsPanel
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />
    </>
  );
};

const AppHeader = ({ children }) => {
  const { isZenMode } = useZenMode();

  return (
    <>
      <TopBar />
      <Box
        component="main"
        sx={{
          pb: { xs: isZenMode ? 0 : `${MOBILE_BOTTOM_NAV_HEIGHT}px`, sm: 0 },
        }}
      >
        {children}
      </Box>
      <MobileBottomNav />
    </>
  );
};

export { resolveHeaderTitle };
export default AppHeader;
